import React, { useEffect, useRef, useState } from 'react'
import { Editor, EditorState, ContentState, convertFromHTML, RichUtils } from "draft-js";
import { stateToHTML } from 'draft-js-export-html';
import { stateFromHTML } from 'draft-js-import-html';
import RedoIcon from '../../images/redo.svg';
import UndoIcon from '../../images/undo.svg';
import BulletListIcon from '../../images/Bullet.svg';
import NumberListIcon from '../../images/Bullet-number.svg';
import ItalicIcon from '../../images/italic.svg';
import LinkIcon from '../../images/link-icon.svg';
import PlusCircleIcon from '../../assets/images/plus-circle.svg';
//import AbcIcon from '../../images/abc.svg';
import 'draft-js/dist/Draft.css';
import SummaryToolTip from './SummaryToolTip';
import { matchParentClass, stripTags, urlWithProtocol } from '../../helper';
import { useResume } from '../../context/ResumeContext';
import DraftEditorDecorator from '../review/DraftEditorDecorator';
import { APP_DOMAIN } from '../../constants/constants';
import { SpellCheckerButton } from './SpellCheckerButton';
import DraftErrorBoundary from './DraftErrorBoundary';
import SafeDOMWrapper from './SafeDOMWrapper';
// import DraftEditorSpellDecorator from '../review/DraftEditorSpellDecorator';

const DraftEditor = (props) => {

    const { resume, handlers, reviewSuggestions, ignoreIt } = useResume()
    const [editorState, setEditorState] = useState();
    const [spellChecking, setCheckSpelling] = useState(false);
    const [spellChecker, setspellChecker] = useState(false);
    const [spellCheckerSchedulerId, setSpellCheckerSchedulerId] = useState();
    const [spellSuggestionList, setSpellSuggestionList] = useState({});
    const [cName, setCName] = useState();
    const [showSummaryToolTip, setShowSummaryToolTip] = useState();
    const [domEditor, setDomEditorRef] = useState();
    const [isFocused, setIsFocused] = useState();
    const [rbaActive, setRbaActive] = useState();
    const [spActive, setSpActive] = useState();
    const [activeWord, setActiveWord] = useState();
    const [suggestionLoader, setSuggestionLoader] = useState(false);
    const suggestionRef = useRef(null);
    useEffect(() => {
        if (!editorState || props.value !== stateToHTML(editorState.getCurrentContent())) {
            //In case of use of suggested Prases & AI Writer
            editorState && props?.reviewEnabled && props?.onBlur()
            //Only first time or content is modified from outside like suggestions, phrases
            //In case of spell word replaced
            if (props.value != '' && spellChecker) setEditorState(EditorState.createWithContent(stateFromHTML(props.value), new DraftEditorDecorator(getSpellSuggestions())?.createWithSpellCheckDecorator()))
            //Already has content
            else if (props.value != '') setEditorState(EditorState.createWithContent(stateFromHTML(props.value), new DraftEditorDecorator()?.createBasicDecorator()))
            else setEditorState(EditorState.createEmpty(new DraftEditorDecorator()?.createBasicDecorator()))
        }
    }, [props.value]);


    /* AI REVIEW SPECIFIC FUNCTION */
    useEffect(() => {
        if (props?.reviewEnabled && spellSuggestionList?.words?.length) {
            const newlist = getSpellSuggestions()
            // console.log('review_change', newlist?.words.length, newlist?.words, spellSuggestionList?.words?.length, spellSuggestionList.words)
            //Update decorator for updated suggestion list
            if (
                spellSuggestionList?.words &&
                (
                    (//Case of last word ignored, 
                        newlist?.words[0] == 'notempty' &&
                        spellSuggestionList?.words[0] != 'notempty'
                    ) || //anytime length will be changed
                    newlist?.words.length !== spellSuggestionList.words?.length
                )
            ) {
                setEditorState(EditorState.set(editorState, { decorator: new DraftEditorDecorator(newlist)?.createWithSpellCheckDecorator() }))
            }
        }
        return () => {
            spellCheckerSchedulerId && clearTimeout(spellCheckerSchedulerId);
            spellCheckerSchedulerId && setSpellCheckerSchedulerId()
            // setspellChecker(false)
        }
    }, [resume?.resume_review]);

    //Specific to spell suggestion box
    useEffect(() => {
        if(activeWord){
            const handleClickOutside = (event) => {
                if (!matchParentClass(event.target, 'suggested-word', 3) &&
                    suggestionRef.current &&
                    !suggestionRef.current.contains(event.target)) {
                    clickedOutside();
                }
            };
            document.addEventListener('click', handleClickOutside, true);
            return () => {
                document.removeEventListener('click', handleClickOutside, true);
            };
        }
    });
    // To handle change in the editor
    const onChange = (editorState) => {
        let currentContent = stateToHTML(editorState.getCurrentContent());
        //strip the specific tags from the current content
        currentContent = stripTags(currentContent, ['code']);
        props.onInputChange(currentContent)
        setEditorState(editorState)
    }

    // To handle key commands
    const handleKeyCommand = (command, editorState) => {
        // Fix: RESUMEORG-REACT-3X - Add defensive error handling for Draft.js internal errors
        try {
            const newState = RichUtils.handleKeyCommand(editorState, command);
            if (newState) {
                onChange(newState);
            }
        } catch (error) {
            // Silently handle Draft.js internal errors (e.g., getType, getBlockForKey failures)
            // These errors occur during edge cases in typing/deletion and don't affect user experience
            console.warn('Draft.js handleKeyCommand error (handled gracefully):', error.message);
            // Return early - don't crash the editor
            return 'not-handled';
        }
    }

    const onBoldClick = (e) => {
        e.preventDefault();
        e.stopPropagation(e);
        onChange(RichUtils.toggleInlineStyle(editorState, 'BOLD'));
    }

    const onItalicsClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        onChange(RichUtils.toggleInlineStyle(editorState, 'ITALIC'));
    }
    const onStrikeClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        onChange(RichUtils.toggleInlineStyle(editorState, 'STRIKETHROUGH'));
    }

    const onUnderlineClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        onChange(RichUtils.toggleInlineStyle(editorState, 'UNDERLINE'));
    }

    const onBulletPointClick = (e) => {
        //console.log('bullet clicked');
        onChange(RichUtils.toggleBlockType(editorState, 'unordered-list-item'));
    }
    const onListingPointClick = (e) => {
        //console.log('bullet clicked');
        onChange(RichUtils.toggleBlockType(editorState, 'ordered-list-item'));
    }

    const onUndoClick = (e) => {
        onChange(EditorState.undo(editorState));
    }
    const onRedoClick = (e) => {
        onChange(EditorState.redo(editorState));
    }
    const onAddLinkClick = (e) => {
        try {
            const selection = editorState.getSelection();
            let link = window.prompt('Enter URL for link -')
            if (!link) {
                this.onChange(RichUtils.toggleLink(editorState, selection, null));
                return 'handled';
            }
            //if(!/^(https?:)?\/\//.test(link)) link = `\\\\${link}`
            link = urlWithProtocol(link)
            const content = editorState.getCurrentContent();
            const contentWithEntity = content.createEntity('LINK', 'MUTABLE', { url: link, target: '_blank' });
            const newEditorState = EditorState.push(editorState, contentWithEntity, 'create-entity');
            const entityKey = contentWithEntity.getLastCreatedEntityKey();
            onChange(RichUtils.toggleLink(newEditorState, selection, entityKey))
        } catch (ex) {
            //To catch exceptions "Got unexpected null or undefined" but don't do anything
            /*
            Error module.exports(rbreact/node_modules/fbjs/lib/nullthrows)
            */
        }
    }

    const onDummyTextClick = (e) => {
        e.preventDefault(e);
        let editorState = EditorState.createWithContent(stateFromHTML(props.dummyValue));
        onChange(editorState);
    }

    // this.setDomEditorRef = ref => {
    //     this.domEditor = ref
    // };

    const focus = () => {
        domEditor.focus();
        setIsFocused(true)
    }

    const handleBlur = () => {
        setIsFocused(false)
    }

    const showAIPopUp = () => {
        props.setShowAIPopup(!props.showAIPopup);
    }

    const showPhrasePopUp = () => {
        props.setShowPhrasePopUp(!props.showPhrasePopUp);
    }

    const btnSpActive = (v) => {
        setSpActive(v)
    }

    const btnRbaActive = (v) => {
        setRbaActive(v)
    }

    const showSummaryToolTipHandler = (v) => {
        setShowSummaryToolTip(v)
    }

    const getClassNames = () => {
        setCName(props.className)
    }
    /* AI REVIEW SPECIFIC FUNCTION */
    const getSpellSuggestions = () => {
        const fld = props.reviewFieldParams
        const errors = reviewSuggestions(fld.section, fld.field, fld.row_id)
        let spell_list = {}, words;
        (errors?.spell_check?.suggestions ?? [{ word: 'notempty', suggested_words: 'notempty1, notempt2' }]).map(v => (spell_list[v.word] = v.suggested_words.split(', ')))
        words = Object.keys(spell_list);
        spell_list = spell_list;
        console.log('getspell', words, spell_list)
        setSpellSuggestionList({ spell_list, words });
        return { spell_list, words, setActiveWord };
    }
    /* AI REVIEW SPECIFIC FUNCTION */
    const spellCheckerToggleHander = (norepeat = false) => {
        if (spellChecker) {
            setEditorState(EditorState.set(editorState, { decorator: new DraftEditorDecorator()?.createBasicDecorator() }))
            if (spellCheckerSchedulerId) {
                clearTimeout(spellCheckerSchedulerId);
                setSpellCheckerSchedulerId();
            }
        } else {
            //Enable docorator to show spell mistake and start scheduler
            setEditorState(EditorState.set(editorState, { decorator: new DraftEditorDecorator(getSpellSuggestions())?.createWithSpellCheckDecorator() }))
            //clear existing schedule
            spellCheckerSchedulerId && clearTimeout(spellCheckerSchedulerId)
            //Load it and then schedule it
            handlers.resumeReview(null, false)
            scheduleSpellChecker()
            setSuggestionLoader(true)
            //Feel like loading
            setTimeout(() => setSuggestionLoader(false) , 2000);

        }
        //toggle
        setspellChecker(!spellChecker)
    }
    /* AI REVIEW SPECIFIC FUNCTION */
    const scheduleFunction = () => {
        //API CALL
        handlers.resumeReview(null, false)
        scheduleSpellChecker()
    }
    /* AI REVIEW SPECIFIC FUNCTION */
    const scheduleSpellChecker = () => {
        const scid = setTimeout(scheduleFunction, 10000);
        setSpellCheckerSchedulerId(scid);
    }


    // }
    // componentDidMount(){
    //     this.setState({
    //         spActive: false,
    //         rbaActive: false,
    //         showSummaryToolTip: false
    //     });
    // }
    // componentWillReceiveProps(newProps) {
    //     /**
    //      * This will reset the state from the new prop if its value is not as same
    //      * as in the editor state. This will help the editor to switch between different
    //      * contents designated in the same DraftEditorComponent component.
    //      */
    //     if (newProps.value !== stateToHTML(this.state.editorState.getCurrentContent())) {
    //         if (newProps.value != '') {
    //             this.setState({
    //                 editorState: EditorState.createWithContent(stateFromHTML(newProps.value)),
    //             });
    //         } else {
    //             this.setState({
    //                 editorState: EditorState.createEmpty()
    //             });
    //         }
    //     }
    // }

    // render() {

    // const {editorState} = this.state;
    // If the user changes block type before entering any text, we can
    // either style the placeholder or hide it. Let's just hide it now.

    const clickedOutside = () => {
        const spellSuggestions = document.querySelector('.spell-suggestions');
        if (spellSuggestions) {
            spellSuggestions.style.display = 'none';
        }
        setActiveWord(null)
    }
    const replaceSpellWord = (word) => {
        //replace word
        let currentContent = stateToHTML(editorState.getCurrentContent());
        //strip the specific tags from the current content
        currentContent = stripTags(currentContent, ['code']);
        currentContent = currentContent.replace(activeWord, word)
        props.onInputChange(currentContent)
        clickedOutside()
        setActiveWord(null)
    }
    const ignoreWord = () => {
        //add activeWord to ignore list
        const fld = props.reviewFieldParams
        const error = reviewSuggestions(fld.section, fld.field, fld.row_id)?.spell_check
        ignoreIt(fld.section, error, activeWord)
        clickedOutside();
        setActiveWord(null)
    }

    const suggestionBoxStyle = {
        position: 'absolute',
        display: 'none',
        background: 'white',
        zIndex: 100
    };
    const pointerAfterStyles = {
        position: 'absolute',
        content: ' ',
        width: 0,
        height: 0,
        borderStyle: 'solid',
        borderWidth: '8px 8.5px 0 8.5px',
        borderColor: '#000 transparent transparent transparent',
        top: '-7px',
        left: '8%',
        transform: 'rotate(180deg)'
    };

    

    let className = 'editor_wrap';
    var contentState = editorState?.getCurrentContent();
    if (contentState && !contentState?.hasText()) {
        if (contentState.getBlockMap().first().getType() !== 'unstyled') {
            className += ' RichEditor-hidePlaceholder';
        }
    }
    return (editorState ? <DraftErrorBoundary>
        <SafeDOMWrapper onError={(error) => console.warn('DraftEditor DOM Error:', error)}>
        <div className={props?.className + (isFocused ? " advanced-text-area focus" : " advanced-text-area")}>
        {<div ref={suggestionRef} className={`spell-suggestions ${(activeWord &&  spellSuggestionList?.spell_list[activeWord]?.length) ? '' : 'hide-important'}`  } style={suggestionBoxStyle} >
            <span style={pointerAfterStyles}></span>
            <div className='spll-pointer'>
            {activeWord && <span style={{ zIndex: 10000,borderRadius:'8px', color: 'black', background: 'white', border: '1px solid #000', display: 'block' }}>
                {spellSuggestionList?.spell_list[activeWord]?.length && <><ul>
                    {spellSuggestionList.spell_list[activeWord]?.map((word, k) => <li key={k} onClick={() => replaceSpellWord(word)}>{word}</li>)}
                </ul>
                    <a className='btn-ignore' onClick={ignoreWord}>
                        <img src={`${APP_DOMAIN}/assets/images/x-mark-red.svg`} /><span>Ignore</span>
                    </a>
                </>
                }</span>
            }
            </div>
        </div>}
        <div className="menubar">
            <div className="menubar-inner">
                <button className="menubar__button bold" onClick={onBoldClick}>B</button>
                <button className="menubar__button italic" onClick={onItalicsClick}><ItalicIcon /></button>
                <button className="menubar__button underline" onClick={onUnderlineClick}>U</button>
                <button className="menubar__button" onClick={onAddLinkClick}><LinkIcon /></button>
                <button className="menubar__button abc" onClick={onStrikeClick}><del>S</del></button>
                <button className="menubar__button bulletedNumList" onClick={onListingPointClick}><NumberListIcon /></button>
                <button className="menubar__button bulletedList abc" onClick={onBulletPointClick}><BulletListIcon /></button>
                {/* <button className="menubar__button abc" onClick={this.setCheckSpelling}><AbcIcon /></button> */}
                <button className="menubar__button undoicon " onClick={onUndoClick}><UndoIcon /></button>
                <button className="menubar__button redoicon" onClick={onRedoClick}><RedoIcon /></button>
                {props?.reviewEnabled && 
                        <SpellCheckerButton
                        spellCheckerToggleHander={spellCheckerToggleHander}
                        suggestionLoader={suggestionLoader}
                        spellChecker={spellChecker}
                    />
                }
            </div>
            <div className="draft-controls">
                <div className="ctrl-btns">
                    {(props.hasOwnProperty('phrasesButton') && props.phrasesButton)
                        ? <div className="phrases-wrap">
                            <button className={`phrases${(props.hasOwnProperty('colorType') && props.colorType) ? (' ' + props.colorType) : ''}${(spActive ? ' active' : '')}`} onClick={
                                () => {
                                    btnSpActive(true);
                                    btnRbaActive(false);
                                    showPhrasePopUp();
                                    if (props.showAIPopup) showAIPopUp();
                                }}><span><i className="plus-circle"><PlusCircleIcon /></i> Suggested Phrases</span></button>
                        </div> : ''}
                    {(props.hasOwnProperty('chatGptButton') && props.chatGptButton)
                        ? <div className="rb-ai-wrap">
                            <button className={`rb-ai${(props.hasOwnProperty('colorType') && props.colorType) ? (' ' + props.colorType) : ''}${(rbaActive ? ' active' : '')}`} onClick={
                                () => {
                                    btnSpActive(false);
                                    btnRbaActive(true);
                                    showAIPopUp();
                                    if (props.showPhrasePopUp) showPhrasePopUp();
                                }}><span><i className="icon-brain"></i> AI Writer </span></button>
                        </div> : ''}
                    {(props.hasOwnProperty('showRegenerateButton') && props.showRegenerateButton)
                        ? <div className="rb-ai-wrap">
                            <button className={`rb-ai${(props.hasOwnProperty('colorType') && props.colorType) ? (' ' + props.colorType) : ''}${(rbaActive ? ' active' : '')}`} onClick={
                                () => {
                                    props.regenerateAction();
                                }}><span><i className="icon-brain"></i> Regenerate </span></button>
                        </div> : ''}
                </div>
                <div className="ctrl-icons">
                    {/* {
                    props.phrasesButton && window.innerWidth < 769 && 
                    <><div className="editor-info-wrap" onClick={() => {
                        this.showResumeBuilder(!this.state.showResumeBuilder);}}>
                        <i className={`editor-exp editor-info${this.state.showResumeBuilder ? ' icon-minus-icon' : ' icon-plus-icon'}`}></i>
                    </div></>
                } */}
                    {
                        props.phrasesButton &&
                        <><div className="editor-info-wrap" onClick={(e) => {
                            if (!showSummaryToolTip) {
                                setShowSummaryToolTip(true);
                            } else {
                                setShowSummaryToolTip(false);
                            }

                        }}>
                            <i className="editor-info icon-info-circle"></i>
                        </div></>
                    }
                    {showSummaryToolTip && <SummaryToolTip showSummaryToolTip={showSummaryToolTip} setShowSummaryToolTip={setShowSummaryToolTip} />}
                </div>
            </div>
        </div>

        <div className={className} onClick={focus}>
            <Editor
                name={props.name}
                placeholder={props.placeholder}
                editorState={editorState}
                onChange={onChange}
                handleKeyCommand={handleKeyCommand}
                ref={setDomEditorRef}
                onBlur={() => {
                    handleBlur();
                    props.onBlur ? props.onBlur() : '';
                }
                }
                spellCheck={spellChecking}
                onFocus={() => {
                    props?.onFocus && props.onFocus();
                    //in case set timout closed but spell check is still on
                    spellChecker && !spellCheckerSchedulerId && scheduleSpellChecker()
                 }}
                //onBlur={props.onBlur}
                preserveSelectionOnBlur={true}
            />
        </div>

    </div>
    </SafeDOMWrapper>
    </DraftErrorBoundary> : null)
}

export default DraftEditor;